// Dialog — the modal-panel primitive, pure. A thin wrapper over
// @radix-ui/react-dialog (modal mode).
//
// Rebuilt from foundations v1 against the design vision (.claude/research/design/06).
// A modal genuinely floats above the page, so it wears the border→elevation
// discipline (§5) at its strongest: no resting edge, an elevated ground
// (--color-bg-elevated) carried by the deepest soft shadow (--shadow-lg), and
// --radius-lg — the restrained large-shell radius, reserved for large floating
// surfaces (a modal, not a content card). The scrim (--color-scrim) dims the page by
// the one shared amount so every overlay reads the same; the scrim sits at
// --z-overlay and the panel at --z-modal, the fixed rungs of the z-ladder.
//
// The hard-a11y contract — focus trap, focus return to the opener, Esc-to-
// dismiss, overlay-click-to-dismiss, `role="dialog"` + `aria-modal`, and the
// aria-labelledby/aria-describedby wiring from Title/Description — is owned by
// Radix. We do NOT hand-roll any of it; this file only shapes and paints the
// surface and supplies a close affordance. When `description` is omitted we
// pass `aria-describedby={undefined}` so Radix suppresses its missing-
// description warning without us inventing hidden text.
//
// Register: no accent is spent here — a container is not "the primary action
// in this view"; the accent lives on whichever action the caller composes
// into `footer`. Everything the panel paints is neutral ink on the elevated
// plane.
//
// Pure/props-driven: fully controlled — `open` + `onOpenChange` hold the
// visibility outside the component, every open/close reported UP (no internal
// open state, no I/O, no domain knowledge). `title`/`description`/`children`/
// `footer` are ReactNode slots the caller owns (and localizes). The primitive
// authors exactly one visible string of its own — the close button's
// accessible name — resolved through the Lingui `t` macro.

import { useLingui } from "@lingui/react/macro";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import type { ReactNode, RefObject } from "react";
import { Icon } from "../Icon/Icon.js";
import "./Dialog.css";

export type DialogProps = {
    // Whether the modal is open. Controlled — the caller owns this state.
    readonly open: boolean;
    // Reports every requested open/close (Esc, overlay click, close button,
    // programmatic). The caller applies it to `open`.
    readonly onOpenChange: (open: boolean) => void;
    // The panel's heading. Wired to aria-labelledby by Radix Title — this is
    // the dialog's accessible name, so it is required.
    readonly title: ReactNode;
    // Optional supporting line under the title. When present it is wired to
    // aria-describedby by Radix Description; when absent the describedby link
    // is explicitly cleared (no phantom description).
    readonly description?: ReactNode;
    // The panel body. Scrolls within the panel when it exceeds the viewport.
    readonly children?: ReactNode;
    // Optional actions row (e.g. a primary Button + a ghost Cancel). The one
    // place the accent may appear in a dialog is a primary action here.
    readonly footer?: ReactNode;
    // Optional extra class on the panel, additive to `bilda-dialog__panel` —
    // an escape hatch for a single caller to scope its own CSS overrides
    // (e.g. a denser header) without touching every dialog. Undefined by
    // default, so every other caller keeps the standard Dialog look.
    readonly className?: string;
    // Optional override of the initial-focus target. Radix's default puts
    // focus on the first tabbable element in DOM order — the header's close
    // button when one is present before the body — which is wrong for a
    // dialog whose body IS a text field the user expects to type into
    // immediately. When set, focus goes here instead on open.
    readonly initialFocusRef?: RefObject<HTMLElement | null>;
    // Optional side column at the panel's end, full height beside the
    // header, body and footer (the New project box's "Your sources"). The
    // caller paints it; absent, the panel is the one column it always was.
    readonly aside?: ReactNode;
};

// Decorative close glyph — the button's meaning is carried by its aria-label,
// so the mark itself is hidden from assistive tech. The ONE registry close
// form (P33).
const closeIcon = <Icon name="close" size="md" />;

export const Dialog = ({
    open,
    onOpenChange,
    title,
    description,
    children,
    footer,
    className,
    initialFocusRef,
    aside,
}: DialogProps) => {
    const { t } = useLingui();
    const panelClass = [
        "bilda-dialog__panel",
        aside == null ? null : "bilda-dialog__panel--aside",
        className ?? null,
    ]
        .filter((c) => c !== null)
        .join(" ");
    const main = (
        <>
            <div className="bilda-dialog__header">
                <DialogPrimitive.Title className="bilda-dialog__title">
                    {title}
                </DialogPrimitive.Title>
                <DialogPrimitive.Close asChild>
                    <button
                        type="button"
                        className="bilda-dialog__close"
                        aria-label={t`Close`}
                    >
                        {closeIcon}
                    </button>
                </DialogPrimitive.Close>
            </div>
            {description != null && (
                <DialogPrimitive.Description className="bilda-dialog__description">
                    {description}
                </DialogPrimitive.Description>
            )}
            {/* tabIndex: the body scrolls when content overflows, and
             * a long informational body may hold nothing focusable —
             * then keyboard users could neither reach nor scroll it
             * (axe scrollable-region-focusable, WCAG 2.1.1). Focusing
             * the body makes arrow/page keys scroll; a :focus-visible
             * ring in the CSS keeps the stop visible. */}
            {/* biome-ignore lint/a11y/noNoninteractiveTabindex: the sanctioned scrollable-region pattern — the div IS interactive (it scrolls); without a tab stop keyboard users can't scroll a body with no focusable children. */}
            <div className="bilda-dialog__body" tabIndex={0}>
                {children}
            </div>
            {footer != null && (
                <div className="bilda-dialog__footer">{footer}</div>
            )}
        </>
    );
    return (
        <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay className="bilda-dialog__overlay" />
                <DialogPrimitive.Content
                    className={panelClass}
                    // No description → clear the describedby link so Radix does
                    // not warn about a missing one (spreading the key only in
                    // that branch; present → Radix wires it from Description).
                    {...(description == null
                        ? { "aria-describedby": undefined }
                        : {})}
                    // Only overridden when a caller supplies a target — every
                    // other Dialog keeps Radix's default (first tabbable
                    // element in DOM order).
                    onOpenAutoFocus={
                        initialFocusRef === undefined
                            ? undefined
                            : (event) => {
                                  const target = initialFocusRef.current;
                                  if (target !== null) {
                                      event.preventDefault();
                                      target.focus();
                                  }
                              }
                    }
                >
                    {aside == null ? (
                        main
                    ) : (
                        <>
                            <div className="bilda-dialog__main">{main}</div>
                            <div className="bilda-dialog__aside">{aside}</div>
                        </>
                    )}
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
};
